import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronDown, Leaf, BookOpen, ClipboardList,
  CheckCircle, ArrowRight, Shield, CreditCard, Clock
} from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import {
  fadeUp, fadeLeft, fadeRight, scaleIn,
  staggerContainer, staggerItem, pageTransition, viewportConfig
} from '../components/AnimationVariants';

const faqs = [
  { q: 'Do you accept new patients?', a: "Yes, we warmly welcome new patients. You can book an appointment by calling 020 8866 5556, requesting a call back, or using our online booking. We will do our best to see you promptly, especially if you are in discomfort." },
  { q: 'What should I expect at my first appointment?', a: "Your first visit is designed to feel relaxed and informative. We begin by getting to know you, your dental history, and your concerns. Your clinician will carry out a thorough examination, explain their findings clearly, and provide a written treatment plan before you decide how to proceed." },
  { q: 'Do you treat nervous patients?', a: "Absolutely. We understand that dental anxiety is very common and we take it seriously. Our team is experienced in helping nervous patients feel comfortable, and we will always explain exactly what we are doing and work at a pace that feels manageable for you." },
  { q: 'How soon can I be seen for an emergency?', a: "Call us immediately on 020 8866 5556 and we will do our best to see you the same day. Whether it is severe toothache, a broken tooth, swelling, or a lost restoration, our priority is to relieve your discomfort and provide clear guidance as quickly as possible." },
  { q: 'Do you offer Invisalign or clear aligners?', a: "Yes. We offer Invisalign® and ClearCorrect® clear aligner systems, planned and overseen by our Specialist Orthodontist. We also offer traditional fixed braces, ceramic braces, and lingual braces. Your clinician will recommend the most suitable option after assessing your teeth, bite, and goals." },
  { q: 'Do you provide dental implants?', a: "Yes. Dental implant treatment is planned using advanced 3D CBCT imaging for precise assessment of bone quality and anatomy. Every treatment begins with a thorough consultation so you understand the process, timescale, and costs involved before anything proceeds." },
  { q: 'Can I spread the cost of treatment?', a: "We believe treatment decisions should feel clear from the start. Before any treatment begins, we explain your options, fees, and likely timescales. For larger plans, we can discuss staged treatment, membership options, or finance options so you can choose the route that feels right for you." },
  { q: 'Will I receive a written treatment plan?', a: "Yes, always. We provide a written treatment plan with clear fees before any treatment begins. There is no pressure to proceed — we want you to feel fully informed and confident in your choices." },
];

const checklist = [
  "Bring a list of any medications you are currently taking",
  "Share details of any medical conditions or allergies we should know about",
  "Bring previous dental X-rays or referral letters if you have them",
  "Write down any questions or concerns you would like to discuss",
  "Let us know if you experience any dental anxiety — we are experienced in helping nervous patients",
  "Tell us what you would like to achieve so we can plan care around your goals",
  "Arrive 10 minutes early if this is your first visit with us",
  "Eat normally before your appointment unless instructed otherwise",
];

const whatToExpect = [
  { step: '1', title: 'Warm Welcome', desc: "You'll be greeted by our front desk team, given a brief tour, and offered water or herbal tea while you settle in." },
  { step: '2', title: 'Health History Review', desc: 'Your hygienist will review your health history, ask about any concerns, and take digital X-rays.' },
  { step: '3', title: 'Comprehensive Exam', desc: "Your clinician will perform a thorough examination — checking for cavities, gum health, bite alignment, and any other concerns — and explain their findings clearly." },
  { step: '4', title: 'Professional Cleaning', desc: "Your hygienist will perform a gentle prophylaxis cleaning to remove plaque and tartar buildup." },
  { step: '5', title: 'Personalized Treatment Plan', desc: "We'll discuss any findings honestly and present a prioritized treatment plan with costs and timelines — no pressure." },
  { step: '6', title: 'Next Steps', desc: "We will confirm any follow-up appointments, answer any remaining questions, and ensure you leave feeling informed and confident about your care plan." },
];

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className="border border-cream-deeper rounded-2xl overflow-hidden bg-white"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4 }}
      viewport={{ once: true }}
    >
      <motion.button
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-cream transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        whileTap={{ scale: 0.99 }}
      >
        <span className="font-serif font-medium text-forest text-base leading-snug">{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}>
          <ChevronDown className="w-5 h-5 text-sage flex-shrink-0" />
        </motion.div>
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1, transition: { height: { duration: 0.3 }, opacity: { duration: 0.25, delay: 0.05 } } }}
            exit={{ height: 0, opacity: 0, transition: { height: { duration: 0.25 }, opacity: { duration: 0.15 } } }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 pt-1 text-gray-600 text-sm leading-relaxed border-t border-cream-deeper">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Resources() {
  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ rotate: [0, 8, 0] }} transition={{ duration: 10, repeat: Infinity }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <BookOpen className="w-4 h-4" /> Patient Resources
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            Everything You Need to Know
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            Preparing for your visit, understanding your treatment, and knowing what to expect — all in one place.
          </motion.p>
        </div>
      </section>

      {/* Quick nav */}
      <section className="py-8 px-4 bg-white border-b border-cream-deeper">
        <motion.div className="container-max flex flex-wrap gap-3 justify-center"
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
          {[
            { label: 'FAQ', icon: BookOpen, href: '#faq' },
            { label: 'First Visit Checklist', icon: ClipboardList, href: '#checklist' },
            { label: 'What to Expect', icon: CheckCircle, href: '#expect' },
            { label: 'Insurance & Payment', icon: Shield, href: '#insurance' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.a key={i} href={item.href} variants={staggerItem}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-forest text-forest text-sm font-medium"
                whileHover={{ backgroundColor: '#1B3A2D', color: '#FFF8F0', scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}>
                <Icon className="w-4 h-4" />{item.label}
              </motion.a>
            );
          })}
        </motion.div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section-padding bg-cream scroll-mt-20">
        <div className="container-max">
          <motion.div className="text-center mb-12" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><Leaf className="w-4 h-4 text-sage" /> Frequently Asked Questions</p>
            <h2 className="section-title mb-4">Your Questions, Answered</h2>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} index={i} />)}
          </div>
        </div>
      </section>

      {/* Checklist + What to Expect */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid md:grid-cols-2 gap-14 items-start">
            <motion.div id="checklist" className="scroll-mt-20" variants={fadeLeft} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <p className="section-subtitle"><ClipboardList className="w-4 h-4 text-sage" /> Before You Arrive</p>
              <h2 className="section-title mb-5">First Visit Checklist</h2>
              <motion.div className="space-y-3"
                variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
                {checklist.map((item, i) => (
                  <motion.div key={i} variants={staggerItem} className="flex items-start gap-3">
                    <motion.div className="w-5 h-5 rounded-full border-2 border-forest flex items-center justify-center flex-shrink-0 mt-0.5"
                      whileHover={{ scale: 1.2, backgroundColor: '#1B3A2D' }}
                      transition={{ type: 'spring', stiffness: 400 }}>
                      <div className="w-2 h-2 rounded-full bg-forest" />
                    </motion.div>
                    <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div id="expect" className="scroll-mt-20" variants={fadeRight} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <p className="section-subtitle"><CheckCircle className="w-4 h-4 text-sage" /> Your Visit</p>
              <h2 className="section-title mb-5">What to Expect</h2>
              <div className="space-y-4">
                {whatToExpect.map((step, i) => (
                  <motion.div key={i} className="flex gap-4"
                    initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }} viewport={{ once: true }}>
                    <motion.div
                      className="w-9 h-9 rounded-full bg-forest flex items-center justify-center text-white font-serif font-bold text-sm flex-shrink-0 mt-0.5"
                      whileHover={{ scale: 1.15, backgroundColor: '#C5A55A' }}
                      transition={{ type: 'spring', stiffness: 400 }}>
                      {step.step}
                    </motion.div>
                    <div>
                      <h4 className="font-serif font-semibold text-forest mb-1">{step.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Insurance */}
      <section id="insurance" className="section-padding bg-cream-dark scroll-mt-20">
        <div className="container-max">
          <motion.div className="text-center mb-12" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><Shield className="w-4 h-4 text-sage" /> Insurance & Payment</p>
            <h2 className="section-title mb-4">Flexible, Transparent Pricing</h2>
          </motion.div>
          <motion.div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {[
              { icon: Shield, title: 'Insurance', desc: 'In-network with Delta Dental, Cigna, MetLife, Aetna, United Healthcare, and many more. We handle all billing and claims.', note: 'We file out-of-network claims too' },
              { icon: CreditCard, title: 'CareCredit Financing', desc: '0% interest financing for 6, 12, or 18 months for qualified applicants. Apply in minutes on the day of your appointment.', note: 'Subject to credit approval' },
              { icon: Clock, title: 'In-House Plans', desc: "Custom payment plans for larger treatment cases. We'll work with you to break treatment into manageable monthly payments.", note: 'No credit check required' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} variants={staggerItem}
                  className="card-wood p-6 text-center"
                  whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(45,90,61,0.12)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                  <motion.div className="w-14 h-14 rounded-2xl bg-sage-pale flex items-center justify-center mx-auto mb-4"
                    whileHover={{ scale: 1.1, backgroundColor: '#1B3A2D' }}
                    transition={{ type: 'spring', stiffness: 400 }}>
                    <Icon className="w-7 h-7 text-forest" />
                  </motion.div>
                  <h3 className="font-serif text-forest font-semibold text-xl mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-3">{item.desc}</p>
                  <span className="text-xs text-sage font-medium italic">{item.note}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-forest-dark to-forest text-center">
        <motion.div className="max-w-2xl mx-auto" variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
          <h2 className="text-3xl font-serif text-white font-semibold mb-4">Still Have Questions?</h2>
          <p className="text-cream/75 mb-7">Our friendly team is happy to help. Give us a call or send a message.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Link to="/contact" className="btn-primary px-8 py-4 text-base">Contact Us <ArrowRight className="w-5 h-5" /></Link>
            </motion.div>
            <motion.a href="tel:+442088665556" className="btn-outline-white px-8 py-4 text-base inline-flex items-center justify-center gap-2"
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              Call 020 8866 5556
            </motion.a>
          </div>
        </motion.div>
      </section>
    </motion.div>
  );
}
